<template>
  <div>
    <div v-if="isLoadingTemplate" class="d-flex justify-content-center align-items-center" style="height: 500px;">
      <v-progress-circular indeterminate color="primary" />
    </div>
    <v-data-table class="table-template" v-else hide-default-footer item-key="id" :headers="headers" :items="items"
      :items-per-page="perPage" no-data-text="No scheduled reports found" fixed-header hide-default-header>
      <template v-slot:header="{ props: { headers } }">
        <tr>
          <th v-for="(header, index) in headers" :key="index"
            :class="{ 'th-no-wrap': ['created_by', 'template_name', 'created_at'].includes(header.value) }"
            :style="headerStyle(header.value)">
            {{ header.text }}
          </th>
        </tr>
      </template>
      <template v-slot:item.name="{ item }">
        <td class="border-bottom-0 pl-0" :style="columnStyle('name')">
          <a
            :href="`/dashboard/scheduled_report/${item.id}`"
            :title="item.name || ''"
            class="schedule-name-link"
          >
            {{ item.name ? item.name : '-' }}
          </a>
        </td>
      </template>

      <template v-slot:item.template_name="{ item }">
        <td class="border-bottom-0 pl-0" :style="columnStyle('template_name')">
          <span class="schedule-cell-text-wrap" :title="item.template_name || ''">{{ item.template_name || '-' }}</span>
        </td>
      </template>

      <template v-slot:item.category="{ item }">
        <td class="border-bottom-0 pl-0" :style="columnStyle('category')">
          <span class="schedule-cell-text" :title="item.category || ''">{{ item.category || '-' }}</span>
        </td>
      </template>

      <template v-slot:item.recipient="{ item }">
        <td class="border-bottom-0 pl-0" :style="columnStyle('recipient')">
          <div class="d-flex align-items-center">
            <span class="schedule-recipient-text">
              <template v-if="firstRecipient(item)">
                <span>{{ firstRecipient(item).email }}</span>
                <span v-if="isRecipientInactive(firstRecipient(item))" class="recipient-inactive-label">(Inactive)</span>
              </template>
              <template v-else>-</template>
            </span>
            <RecipientTooltip v-if="moreRecipients(item).length > 0" :hoverMode="true" direction="bottom" :nudge="-5">
              <template #trigger>
                <span class="text-primary ml-1">+{{ moreRecipients(item).length }} more</span>
              </template>
              <template #text>
                <div class="recipient-tooltip">
                  <div v-for="(recipient, idx) in moreRecipients(item)" :key="idx" class="recipient-tooltip__item">
                    <span class="recipient-tooltip__email">{{ recipient.email }}</span>
                    <span v-if="isRecipientInactive(recipient)" class="recipient-inactive-label">(Inactive)</span>
                  </div>
                </div>
              </template>
            </RecipientTooltip>
          </div>
        </td>
      </template>

      <template v-slot:item.frequency="{ item }">
        <td class="border-bottom-0 pl-0" :style="columnStyle('frequency')">
          <span style="color: #3F3F3F;">{{ item.frequency || '-' }}</span>
        </td>
      </template>

      <template v-slot:item.created_by="{ item }">
        <td class="border-bottom-0 pl-0" :style="columnStyle('created_by')">
          <div class="d-flex justify-content-center align-items-center" style="gap: 8px;">
            <CSquareAvatar :user="item.created_by" :size="28"></CSquareAvatar>
          </div>
        </td>
      </template>

      <template v-slot:item.status="{ item }">
        <td class="border-bottom-0 pl-0" :style="columnStyle('status')">
          <StatusBadge
            v-if="item.status"
            type="clients"
            :status="item.status.toString().toLowerCase()"
          />
          <span v-else>-</span>
        </td>
      </template>

      <template v-slot:item.created_at="{ item }">
        <td class="border-bottom-0 pl-0" :style="columnStyle('created_at')">
          <span class="schedule-cell-text">{{ formatShortDate(item.created_at) }}</span>
        </td>
      </template>

      <template v-slot:item.action="{ item }">
        <td class="border-bottom-0 pl-0" :style="columnStyle('action')">
          <div class="schedule-action-row d-flex" style="gap: 8px; float: right;">
            <button @click="runReport(item)" class="btn btn-outline-primary btn-sm schedule-action-btn">
              Run Report
            </button>
            <button @click="viewScheduledReport(item.id)" class="btn btn-primary btn-sm schedule-action-btn">
              Preview
            </button>
            <!-- v-menu with trigger button -->
            <v-menu content-class="schedule-action-menu" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <button v-bind="attrs" v-on="on" style="border:1px solid #D7D7D7" class="btn btn-outline btn-sm schedule-action-btn">
                  <span class="mdi mdi-dots-horizontal"></span>
                </button>
              </template>
              <div>
                <div class="schedule-action-item" @click="toggleStatus(item)">
                  <span class="schedule-action-title">{{ toggleLabel(item.status) }}</span>
                </div>
              </div>
            </v-menu>
          </div>
        </td>
      </template>
    </v-data-table>
    <TablePagination v-if="totalItems > 0" @loadData="loadData" @loadPerPage="loadPerPage" :perPage="perPage"
      :totalItems="totalItems" showDropdownPerPage title="report template(s)" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import TablePagination from '../table-pagination.vue';
import CConfirmation from '../c-confirmation.vue'
import moment from 'moment-timezone';
import { formatFilterDateValue } from '../../shared/report-filter-helpers';
import RecipientTooltip from './RecipientTooltip.vue'
import CSquareAvatar from '../c-square-avatar'
import StatusBadge from '../StatusBadge.vue';
// import RunReportModal from './RunReportModal.vue';

export default {
  props: ['items', 'headers', 'perPage', 'totalItems'],
  components: {
    TablePagination,
    CConfirmation,
    RecipientTooltip,
    CSquareAvatar,
    StatusBadge,
  },
  data() {
    return {
      modal: {
        flag: false,
        data: null,
      }
    }
  },
  computed: {
    ...mapGetters({
      isLoadingTemplate: "reportSchedules/isLoadingTemplate",
      timeZone: "reportSchedules/timeZone",
    }),
  },
  methods: {
    ...mapActions({
      toggleReportScheduleStatus: "reportSchedules/toggleReportScheduleStatus",
      runReportSchedule: "reportSchedules/runReportSchedule",
    }),
    columnWidth(column) {
      const header = (this.headers || []).find((item) => item?.value === column);
      const width = Number(header?.width);
      if (!Number.isFinite(width) || width <= 0) return null;
      return width;
    },
    columnStyle(column) {
      const width = this.columnWidth(column);
      if (!width) return {};
      const px = `${width}px`;
      return { width: px, minWidth: px, maxWidth: px };
    },
    headerStyle(column) {
      return {
        ...this.columnStyle(column),
        fontSize: '14px',
      };
    },
    recipientList(item) {
      return (item?.recipients || []).filter((recipient) => recipient && recipient.email);
    },
    firstRecipient(item) {
      return this.recipientList(item)[0] || null;
    },
    moreRecipients(item) {
      const list = this.recipientList(item);
      return list.length > 1 ? list.slice(1) : [];
    },
    isRecipientInactive(recipient) {
      if (!recipient) return false;
      const status = (recipient?.status || '').toString().toLowerCase();
      return status === 'inactive';
    },
    loadData(newPage) {
      this.$emit('loadData', newPage);
    },
    loadPerPage(perPage) {
      this.$emit('loadPerPage', perPage);
    },
    formatDate(date) {
      if (date) {
        return moment(date, 'YYYY-MM-DDTHH:mm:SSSZZ').tz(this.timeZone).format('MM/DD/YYYY HH:mm')
      }
      else {
        return ''
      }
    },
    formatShortDate(date) {
      if (!date) return '-';
      const base = moment(date, 'YYYY-MM-DDTHH:mm:SSSZZ');
      const zoned = this.timeZone ? base.tz(this.timeZone) : base;
      return zoned.format('MM/DD/YYYY');
    },
    parsedFilters(filterObj) {
      const result = [];
      for (const key in filterObj) {
        if (key === 'sort') continue;

        const value = filterObj[key];
        if (key === 'dynamic_filters' && Array.isArray(value)) {
          value.forEach((entry) => {
            if (!entry?.key || !entry?.value) return;
            const displayValue = formatFilterDateValue(entry.key, entry.value);
            result.push({
              key: entry.key,
              value: entry.value,
              display: displayValue,
              fullDisplay: displayValue,
            });
          });
          continue;
        }

        if (Array.isArray(value) && value.length > 0) {
          result.push({
            key: this.formatKey(key),
            value,
            display: `${value.length} selected`,
            fullDisplay: value.join(', '),
          });
        } else if (typeof value === 'string' && value.trim() !== '') {
          const displayValue = formatFilterDateValue(key, value);

          result.push({
            key: this.formatKey(key),
            value,
            display: displayValue,
            fullDisplay: displayValue,
          });
        }
      }
      return result;
    },
    formatKey(key) {
      return key
        .replace(/_?ids?$/i, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
        .trim();
    },
    previewReport(id) {
      window.location.href = `/dashboard/report_list/${id}`;
    },
    runReport(item) {
      if (!item?.id) return;
      this.runReportSchedule({ id: item.id, params: {} });
      this.$emit('showBanner', 'Document is being processed, check progress on process log!');
    },
    closeModal() {
      this.modal = {
        flag: false,
        data: null,
      }
    },
    scheduleReport(id) {
      window.location.href = `/dashboard/report_list/${id}/new_scheduled_report`;
    },
    viewScheduledReport(id) {
      window.location.href = `/dashboard/scheduled_report/${id}`;
    },
    toggleLabel(status) {
      const normalized = (status || '').toString().toLowerCase();
      return normalized === 'active' ? 'Deactivate' : 'Activate';
    },
    toggleStatus(item) {
      if (!item?.id) return;
      this.toggleReportScheduleStatus(item.id).then(() => {
        const status = (item.status || '').toString().toLowerCase();
        const message = status === 'active'
          ? 'Scheduled Report successfully Deactivated'
          : 'Scheduled Report successfully Activated';
        this.$emit('showBanner', message);
        this.$emit('reloadData');
      });
    },
  },
};
</script>

<style scoped lang="scss">
.v-data-table th {
  background-color: #F4F4F4 !important;
  color: #4f4f4f;
}

.th-no-wrap {
  white-space: nowrap;
}

::v-deep .v-data-table__wrapper {
  padding-bottom: 0 !important;
  margin-bottom: 20px !important;
}

::v-deep .table-template table {
  table-layout: fixed;
  width: 100%;
}

.btn:hover .v-icon {
  color: #fff !important;
}

::v-deep .btn-outline-primary:hover {
  background-color: white !important;
  color: #007bff;
}

.v-menu__content {
  z-index: 999 !important;
  width: auto;
}

.tooltip {
  padding: 10px;
  border-radius: 2px;
  opacity: 0;
  min-width: 200px;
  max-width: 400px;
  color: black !important;
  transition: opacity 1s;
  position: absolute;
  z-index: 2147483647 !important;
  background: #ffffff;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.5);
  border-radius: 5px;
}

.icon-name {
  border-radius: 8px;
  background-color: #DFA26C;
  padding: 4px;
}

::v-deep .table-template tbody tr {
  height: auto;
}

.custom-tooltip-container {
  border-radius: 8px;
  color: #3F3F3F !important;
}

.custom-tooltip-header {
  background-color: #F4F4F4;
  padding: 8px 16px;
}

.custom-badge-primary {
  background-color: #DDF0FF;
  border-radius: 8px;
  padding: 2px 8px;
  color: #007BFF;
  font-size: 12px;
  font-weight: bold;
  width: fit-content;
  height: fit-content;
  text-wrap: nowrap;
  cursor: pointer;
}

.custom-badge-secondary {
  background-color: #EDF4FE;
  border-radius: 8px;
  padding: 2px 8px;
  color: #3F3F3F;
  font-size: 12px;
  width: fit-content;
  height: fit-content;
  display: block;
}

.avatar-circle {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 600;
}

.recipient-tooltip {
  max-height: 240px;
  overflow-y: auto;
  min-width: 200px;
  padding-right: 10px;
  box-sizing: border-box;
}

.recipient-tooltip__item {
  font-size: 12px;
  color: #3F3F3F;
  padding: 4px 8px 4px 0;
  border-bottom: 1px solid #F0F0F0;
  overflow-wrap: anywhere;
}

.recipient-tooltip__email {
  color: #3F3F3F;
}

.recipient-tooltip__item:last-child {
  border-bottom: none;
}

.recipient-inactive-label {
  margin-left: 4px;
  color: #F44336;
  font-weight: 600;
}

::v-deep .table-template .v-data-table__empty-wrapper {
  height: 240px !important;
}

::v-deep .table-template .v-data-table__empty-wrapper td {
  vertical-align: top;
  padding-top: 150px;
}

.schedule-name-link {
  display: inline-block;
  color: #007BFF;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  vertical-align: middle;
}

.schedule-cell-text {
  color: #3F3F3F;
  display: inline-block;
  vertical-align: middle;
}

.schedule-cell-text-wrap {
  color: #3F3F3F;
  display: inline-block;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  vertical-align: middle;
}

.schedule-recipient-text {
  color: #3F3F3F;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.schedule-action-row {
  flex-wrap: nowrap;
  align-items: center;
  white-space: nowrap;
}

.schedule-action-btn {
  white-space: nowrap;
}

::v-deep .schedule-action-menu {
  width: 145px !important;
  min-width: 140px !important;
}

::v-deep .schedule-action-title {
  font-size: 14px !important;
  line-height: 20px !important;
  margin: 12px;
}

::v-deep .schedule-action-item{
  padding: 10px 0px;
  cursor: pointer;
}
::v-deep .schedule-action-item:hover{
  padding: 10px 0px;
  background-color: rgb(237, 237, 237);
}

.v-list{
  padding: 0px!important;
  border-radius: 5px;
  border: 1px solid #e2e2e2;
}

</style>

<style>
.report-templates .v-tooltip__content {
  background: white !important;
  background-color: white !important;
  color: #3F3F3F !important;
  box-shadow: 0px 3px 4px 0px #00000026;
  padding: 0 !important;
  border-radius: 8px;
}
</style>
